import cx from 'classnames'
import { Button } from '@/components/Button'
import { PageTip } from '@/components/PageTip'
import { type ReactNode, type Ref } from 'react'
import styles from './ContentLayout.module.css'
import type { FittedIllustration } from '@/components/SpotIllustration/fitted-illustration'

interface ContentLayoutProps {
  children?: ReactNode
  /**
   * Fills the remaining height in the parent, if the parent also does this.
   */
  fill?: boolean
  /**
   * Use this if the header renders a tab bar.
   */
  accountForTabBar?: boolean
  /**
   * Use this if the content renders a section-like label as the first element.
   */
  accountForLabel?: boolean
  /**
   * Removes all padding from root styling.
   */
  noPadding?: boolean
  ref?: Ref<HTMLDivElement>
}

/**
 * Pads a page's content below its header.
 */
export function ContentLayout({
  children,
  accountForTabBar,
  accountForLabel,
  fill,
  noPadding,
  ref,
}: ContentLayoutProps) {
  return (
    <div
      ref={ref}
      className={cx(
        styles.root,
        accountForTabBar && styles.accountForTabBar,
        accountForLabel && styles.accountForLabel,
        fill && styles.fill,
        noPadding && styles.noPadding,
      )}
    >
      {children}
    </div>
  )
}

interface ContentMainProps {
  children?: ReactNode
  /**
   * Fill the available space in the parent.
   */
  fill?: boolean
  /**
   * Constrain the width to not be more than the parent's width.
   */
  constrainWidth?: boolean
}

/**
 * The main column of a content layout.
 */
export function ContentMain({
  children,
  fill,
  constrainWidth,
}: ContentMainProps) {
  return (
    <div
      className={cx(
        styles.main,
        fill && styles.fill,
        constrainWidth && styles.constrainWidth,
      )}
    >
      {children}
    </div>
  )
}

interface ContentTipProps {
  title: string
  children?: ReactNode
  helpCenterUrl?: string
  illustrationUrl?: string
  /**
   * Shown instead of `illustrationUrl` when set.
   */
  illustration?: FittedIllustration
  /**
   * Hides the illustration, for workspaces in corporate mode.
   */
  corporateMode: boolean
}

/**
 * A page tip shown beside the main column.
 */
export function ContentTip({
  children,
  title,
  illustrationUrl,
  illustration,
  helpCenterUrl,
  corporateMode,
}: ContentTipProps) {
  return (
    <div className={cx(styles.tip)}>
      <PageTip
        title={title}
        illustrationUrl={illustrationUrl}
        illustration={illustration}
        corporateMode={corporateMode}
        button={
          helpCenterUrl && (
            <Button
              label="Visit our Help Center"
              variant="primary"
              href={helpCenterUrl}
              target="_blank"
            />
          )
        }
      >
        {children}
      </PageTip>
    </div>
  )
}

interface ContentEmptyProps {
  children?: ReactNode
}

/**
 * Placeholder shown when the content has nothing to list.
 */
export function ContentEmpty({ children }: ContentEmptyProps) {
  return <div className={styles.empty}>{children}</div>
}
